/**
 * Order → Payments tab (mockup docs/design/marketplace-hire/Payment.dc.html):
 * where the money is, stage by stage — what the client paid Twasta, or for the
 * builder what each stage pays out after Twasta's fee, the tax on it and TDS —
 * the monthly price after install, and the order's invoices.
 */
import { fmtDate, money } from '@/api/marketplace'
import { orderInvoiceUrl, payStage, type Order, type OrderStage } from '@/api/marketplaceBuild'
import HostCallout from './HostCallout'
import PaymentStages from './PaymentStages'
import { MoneyBar, StagePill, currentStage, doneCount, firstName, hasMonthly, monthlyText, type OrderCtx } from './orderShared'

export default function OrderPaymentsTab(ctx: OrderCtx) {
  const { order } = ctx
  const client = order.role === 'client'
  const cur = currentStage(order)
  return (
    <div className="mkt-col" style={{ gap: 20 }}>
      {client && cur?.status === 'payment_due' && <DueBanner ctx={ctx} stage={cur} />}

      <section className="mkt-panel mkt-col" aria-label="Where the money is">
        <h2>Where the money is</h2>
        <span className="mkt-muted" style={{ fontSize: 14 }}>
          {client
            ? `You pay Twasta; Twasta pays ${order.builder.name} for each stage after you approve it.`
            : `${order.client.name} pays Twasta; Twasta pays you for each stage after they approve it.`}
        </span>
        <MoneyBar order={order} />
        <PaymentStages stages={order.stages} rows={order.stages} currency={order.currency}
          who={order.role === 'admin' ? 'admin' : order.role} done={doneCount(order)} review={cur?.status === 'in_review'}
          advanceRelease={order.terms.advance_release} />
      </section>

      {client ? <ClientSchedule ctx={ctx} /> : <BuilderSchedule order={order} />}

      {client && <NextPayment order={order} />}

      {hasMonthly(order.monthly) && (
        <section className="mkt-panel mkt-col" aria-label="After the install">
          <h2>After the install</h2>
          <span className="mkt-muted">
            {client
              ? `Once you sign off, the app costs ${monthlyText(order.monthly, order.currency)}, taken from your organization wallet like your other apps.`
              : `Once ${order.client.name} signs off, they pay ${monthlyText(order.monthly, order.currency)} to use the app.`}
          </span>
          <HostCallout currency={order.currency} />
        </section>
      )}

      <Invoices order={order} />

      <span className="mkt-faint">
        {client
          ? 'GST was charged when your organization wallet was topped up, so stage payments and service fees carry no second GST line.'
          : order.terms.builder_fee_tax === 'added'
            ? 'Tax on Twasta’s fee is added on top of the fee. Twasta pays your earnings to your bank account.'
            : 'Tax on Twasta’s fee is included in the fee. Twasta pays your earnings to your bank account.'}
      </span>
    </div>
  )
}

function DueBanner({ ctx, stage }: { ctx: OrderCtx; stage: OrderStage }) {
  const { order, act, busy } = ctx
  return (
    <div className="mkt-banner bad" role="status">
      <div>
        <strong>Payment needed — {money(stage.client.total_minor, order.currency)} for “{stage.name}”</strong>
        {firstName(order.builder.person, order.builder.name)} submitted it. Your organization wallet didn’t cover it, so your review starts once it is paid.
        {!order.approver && ' An administrator or project owner of your organization can pay it.'}
      </div>
      {order.can.pay && (
        <button type="button" className="btn btn-primary" disabled={busy}
          onClick={() => act(() => payStage(order.id, stage.idx), 'Paid — your review has started')}>
          Pay {money(stage.client.total_minor, order.currency)}
        </button>
      )}
    </div>
  )
}

function paidIn(s: OrderStage, order: Order): { text: string; tone: string } {
  if (s.status === 'refunded') return { text: 'Refunded', tone: '' }
  if (s.collected_at) return { text: `Paid ${fmtDate(s.collected_at)}`, tone: 'ok' }
  if (s.status === 'payment_due') return { text: 'Payment needed', tone: 'bad' }
  if (s.idx === order.stages[0]?.idx) return { text: 'On order', tone: '' }
  return { text: order.terms.collect_on === 'approve' ? 'When you approve' : 'When submitted', tone: '' }
}

function paidOut(s: OrderStage): { text: string; tone: string } {
  switch (s.status) {
    case 'released': return { text: `Paid ${fmtDate(s.released_at)}`, tone: 'ok' }
    case 'approved': return { text: 'Approved — being paid', tone: 'info' }
    case 'in_review': return { text: 'Held — your approval', tone: 'warn' }
    case 'changes_requested': return { text: 'Held — changes asked', tone: 'warn' }
    case 'disputed': return { text: 'Frozen — disputed', tone: 'bad' }
    case 'refunded': return { text: 'Refunded to you', tone: '' }
    default: return { text: 'Not yet', tone: '' }
  }
}

function ClientSchedule({ ctx }: { ctx: OrderCtx }) {
  const { order } = ctx
  const c = order.currency
  const sum = (f: (s: OrderStage) => number) => order.stages.reduce((n, s) => n + f(s), 0)
  return (
    <section className="mkt-panel mkt-col" aria-label="Stage by stage">
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <h2>Stage by stage</h2>
        <span className="mkt-faint">
          Each share is collected {order.terms.collect_on === 'approve' ? 'when you approve that stage' : `when ${firstName(order.builder.person, order.builder.name)} submits that stage`}
        </span>
      </div>
      <div className="mkt-scroll">
        <table className="mkt-table">
          <thead>
            <tr>
              <th scope="col">Stage</th><th scope="col">Share</th><th scope="col">You paid Twasta</th>
              <th scope="col">Twasta paid {firstName(order.builder.person, order.builder.name)}</th>
              <th scope="col" className="num">Amount</th><th scope="col" className="num">Service fee</th><th scope="col" className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {order.stages.map((s) => {
              const pin = paidIn(s, order)
              const pout = paidOut(s)
              return (
                <tr key={s.idx}>
                  <td><strong>{s.name}</strong></td>
                  <td>{s.pct}%</td>
                  <td><span className={`mkt-pill ${pin.tone}`}>{pin.text}</span></td>
                  <td><span className={`mkt-pill ${pout.tone}`}>{pout.text}</span></td>
                  <td className="num">{money(s.client.base_minor, c)}</td>
                  <td className="num">{money(s.client.service_fee_minor, c)}<div className="mkt-faint">{s.client.service_fee_percent}%</div></td>
                  <td className="num"><strong>{money(s.client.total_minor, c)}</strong></td>
                </tr>
              )
            })}
            <tr>
              <td colSpan={4}><strong>Total</strong></td>
              <td className="num"><strong>{money(sum((s) => s.client.base_minor), c)}</strong></td>
              <td className="num"><strong>{money(sum((s) => s.client.service_fee_minor), c)}</strong></td>
              <td className="num"><strong>{money(sum((s) => s.client.total_minor), c)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mkt-kpis">
        <div className="mkt-kpi"><span className="mkt-muted">Paid to Twasta so far</span><strong>{money(order.money.collected_minor + order.money.service_fees_minor, c)}</strong><span className="mkt-faint">{money(order.money.service_fees_minor, c)} of it service fees</span></div>
        <div className="mkt-kpi"><span className="mkt-muted">Still to pay</span><strong>{money(order.money.still_to_pay_minor, c)}</strong><span className="mkt-faint">plus the service fee on each share</span></div>
        {order.money.refunded_minor > 0 && <div className="mkt-kpi"><span className="mkt-muted">Refunded to your wallet</span><strong>{money(order.money.refunded_minor, c)}</strong></div>}
      </div>
    </section>
  )
}

function BuilderSchedule({ order }: { order: Order }) {
  const c = order.currency
  const p0 = order.stages.find((s) => s.payout)?.payout
  const sum = (f: (s: OrderStage) => number) => order.stages.reduce((n, s) => n + f(s), 0)
  return (
    <section className="mkt-panel mkt-col" aria-label="Your payout, stage by stage">
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <h2>Your payout, stage by stage</h2>
        <span className="mkt-faint">Paid into your Twasta earnings when {order.client.name} approves each stage</span>
      </div>
      <div className="mkt-scroll">
        <table className="mkt-table">
          <thead>
            <tr>
              <th scope="col">Stage</th>
              <th scope="col" className="num">Stage value</th>
              <th scope="col" className="num">Twasta fee{p0 ? ` (${p0.fee_percent}%)` : ''}</th>
              <th scope="col" className="num">Tax on fee</th>
              <th scope="col" className="num">TDS{p0 ? ` (${p0.tds_percent}%)` : ''}</th>
              <th scope="col" className="num">You get</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {order.stages.map((s) => {
              const p = s.payout
              return (
                <tr key={s.idx}>
                  <td><strong>{s.name}</strong> <span className="mkt-faint">· {s.pct}%</span></td>
                  <td className="num">{money(p?.gross_minor ?? s.amount_minor, c)}</td>
                  <td className="num">{p ? `− ${money(p.fee_minor, c)}` : '—'}</td>
                  <td className="num">
                    {p ? `− ${money(p.fee_tax_minor, c)}` : '—'}
                    {p && p.fee_tax_breakdown.length > 0 && <div className="mkt-faint">{p.fee_tax_breakdown.map((t) => `${t.name} ${t.rate}%`).join(' + ')}</div>}
                  </td>
                  <td className="num">{p ? `− ${money(p.tds_minor, c)}` : '—'}</td>
                  <td className="num">
                    <strong>{money(s.released_net_minor ?? p?.net_minor ?? null, c)}</strong>
                    {s.released_net_minor != null && <div className="mkt-faint">paid</div>}
                  </td>
                  <td><StagePill stage={s} role={order.role} />{s.released_at && <div className="mkt-faint">{fmtDate(s.released_at)}</div>}</td>
                </tr>
              )
            })}
            <tr>
              <td><strong>Total</strong></td>
              <td className="num"><strong>{money(sum((s) => s.payout?.gross_minor ?? s.amount_minor), c)}</strong></td>
              <td className="num"><strong>− {money(sum((s) => s.payout?.fee_minor ?? 0), c)}</strong></td>
              <td className="num"><strong>− {money(sum((s) => s.payout?.fee_tax_minor ?? 0), c)}</strong></td>
              <td className="num"><strong>− {money(sum((s) => s.payout?.tds_minor ?? 0), c)}</strong></td>
              <td className="num"><strong>{money(sum((s) => s.payout?.net_minor ?? 0), c)}</strong></td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mkt-kpis">
        <div className="mkt-kpi"><span className="mkt-muted">Paid to you so far</span><strong>{money(order.money.paid_to_builder_minor, c)}</strong><span className="mkt-faint">after fees, tax and TDS</span></div>
        <div className="mkt-kpi"><span className="mkt-muted">Held by Twasta for you</span><strong>{money(order.money.held_minor, c)}</strong><span className="mkt-faint">paid when the client approves</span></div>
        {(order.money.tds_minor > 0 || (p0?.tds_percent ?? 0) > 0) && (
          <div className="mkt-kpi"><span className="mkt-muted">TDS withheld</span><strong>{money(order.money.tds_minor, c)}</strong><span className="mkt-faint">withheld by Twasta for tax and deposited for you</span></div>
        )}
      </div>
    </section>
  )
}

function NextPayment({ order }: { order: Order }) {
  if (order.status !== 'in_progress' && order.status !== 'placed') return null
  const next = order.stages.find((s) => !s.collected_at && (s.status === 'pending' || s.status === 'changes_requested'))
  if (!next) return null
  const builderFirst = firstName(order.builder.person, order.builder.name)
  return (
    <section className="mkt-panel mkt-row" aria-label="Next payment" style={{ justifyContent: 'space-between' }}>
      <span className="mkt-col" style={{ gap: 2 }}>
        <strong>Next payment</strong>
        <span className="mkt-muted" style={{ fontSize: 14 }}>
          {next.name} · {next.pct}%{next.target_date ? ` · expected around ${fmtDate(next.target_date)}` : ''}
        </span>
        <span className="mkt-faint">
          Collected from your organization wallet {order.terms.collect_on === 'approve' ? 'when you approve it' : `when ${builderFirst} submits it`}. If the wallet is short, you’ll be asked to pay.
        </span>
      </span>
      <strong style={{ fontSize: 22 }}>{money(next.client.total_minor, order.currency)}</strong>
    </section>
  )
}

function Invoices({ order }: { order: Order }) {
  const list = order.invoices ?? []
  const fee = order.role === 'builder'
  return (
    <section className="mkt-panel mkt-col" aria-label="Invoices">
      <h2>{fee ? 'Twasta’s fee invoices' : 'Invoices and receipts'}</h2>
      {list.length === 0 ? (
        <span className="mkt-muted">Invoices appear here after each payment.</span>
      ) : (
        <div className="mkt-scroll">
          <table className="mkt-table">
            <thead>
              <tr>
                <th scope="col">Invoice</th><th scope="col">Date</th><th scope="col">For</th>
                {list.some((i) => i.kind === 'build_fee') && <th scope="col" className="num">Tax</th>}
                <th scope="col" className="num">Total</th><th scope="col"><span className="mkt-sr">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {list.map((i) => (
                <tr key={i.number}>
                  <td><strong>{i.number}</strong></td>
                  <td>{i.issued_at ? fmtDate(i.issued_at) : '—'}</td>
                  <td>{i.note || (i.kind === 'build_fee' ? 'Twasta fee' : 'Stage payment')}</td>
                  {list.some((x) => x.kind === 'build_fee') && <td className="num">{i.kind === 'build_fee' ? money(i.tax_minor, i.currency) : '—'}</td>}
                  <td className="num">{money(i.total_minor, i.currency)}</td>
                  <td className="num"><a href={orderInvoiceUrl(order.id, i.number)} target="_blank" rel="noopener">Open<span className="mkt-sr"> invoice {i.number}</span></a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
